import type { SagaIterator } from 'redux-saga';
import type { AllEffect, ForkEffect } from 'redux-saga/effects';
import { all, call, cancelled, delay, fork, put, race, select, take, takeLatest } from 'redux-saga/effects';
import { client } from '@studio/api/api-client/client';
import { STALE_DATA_TIMEOUT } from '@studio/constants/api-constants';
import { CENTRAL_API_V2, CENTRAL_API_V3 } from '@studio/constants/env-variables';
import { customerBridge } from '@studio/storeV2/customer/customer-bridge';
import type { Customer } from '@studio/types';
import type { ApiClientConfig } from '@studio/types/axios';
import { fetchMeAsync, selectAccessToken, selectCustomerId } from '../authentication';
import { fetch, set, poll } from './actions';
import { selectCustomerTimestamp, selectCustomerData, selectIsPolling } from './selectors';
import type { FirstTimeSetupStatus, CustomerNames } from './types';
import { CustomerActionTypes } from './types';

function* fetchCustomer(action: ReturnType<typeof fetch.customer.request>): SagaIterator {
  const timestamp: number = yield select(selectCustomerTimestamp);
  if (!action.payload?.force && Date.now() - timestamp < STALE_DATA_TIMEOUT) {
    yield put(fetch.customer.cancel());
    return;
  }
  const customerId: string = action.payload?.uuid || (yield select(selectCustomerId));
  const token: string = yield select(selectAccessToken);
  const url = `${CENTRAL_API_V3}/customers/${customerId}`;
  const config: ApiClientConfig = { token };

  customerBridge.customer.request();
  try {
    const response: Customer.Get.Customer = yield call(client, url, config);

    const {
      multisitePlanName,
      multisiteStartDate,
      subscriptionDetails,
      vodPlanName,
      vodStartDate,
      webPlanName,
      webStartDate,
    } = response;

    try {
      yield call(pendo.identify, {
        visitor: { id: pendo.getVisitorId() },
        account: {
          id: pendo.getAccountId(),
          multi_plan: multisitePlanName ?? '',
          multi_start_date: multisiteStartDate ?? '',
          live_plan: webPlanName ?? '',
          live_start_date: webStartDate ?? '',
          content_library_plan: vodPlanName ?? '',
          content_library_start_date: vodStartDate ?? '',
          subtitles: Boolean(subscriptionDetails.subtitlesHoursPerMonth),
        },
      });
    } catch (pendoError) {
      console.error('error calling pendo.identify', pendoError);
    }

    yield put(fetch.customer.success(response));
    yield put(set.timestamp(Date.now()));
    customerBridge.customer.success(response);
    yield put(fetch.fts.request());
    yield take(CustomerActionTypes.FETCH_REQUEST_SUCCESS_FTS);
  } catch (error) {
    console.log(error);
    if (error instanceof Error) {
      yield put(fetch.customer.failure(error));
      customerBridge.customer.failure(error);
    }
  }
}

function* fetchCustomerNames(): SagaIterator {
  const source = new AbortController();
  const token: string = yield select(selectAccessToken);
  const url = `${CENTRAL_API_V2}/customers/names`;
  const config: ApiClientConfig = { token };

  customerBridge.customerNames.request();
  try {
    const response: CustomerNames[] = yield call(client, url, config);
    yield put(fetch.customerNames.success(response));
    customerBridge.customerNames.success(response);
  } catch (error) {
    if (error instanceof Error) {
      yield put(fetch.customerNames.failure(error));
      customerBridge.customerNames.failure(error);
    }
  } finally {
    if (yield cancelled()) {
      source.abort('cancelled');
      yield put(fetch.customerNames.cancel());
    }
  }
}

function* fetchCustomerRefresh(action: ReturnType<typeof fetch.customerRefresh.request>): SagaIterator {
  const source = new AbortController();
  const customerId: string = action.payload?.uuid || (yield select(selectCustomerId));
  const token: string = yield select(selectAccessToken);
  const url = `${CENTRAL_API_V3}/customers/${customerId}`;
  const config: ApiClientConfig = { token, signal: source.signal };

  try {
    const response: Customer.Get.Customer = yield call(client, url, config);
    // orgs have switched corner case
    const currentCustomerId = yield select(selectCustomerId);
    if (response.uuid !== currentCustomerId) {
      return;
    }

    yield put(fetch.customerRefresh.success(response));
    yield put(set.timestamp(Date.now()));
    customerBridge.customer.success(response);
  } catch (error) {
    console.log(error);
    yield put(fetch.customerRefresh.failure(error));
    if (error instanceof Error) {
      customerBridge.customer.failure(error);
    }
  } finally {
    if (yield cancelled()) {
      source.abort('cancelled');
      yield put(fetch.customerRefresh.cancel());
    }
  }
}

function* fetchFtsStatus(): SagaIterator {
  const source = new AbortController();
  const customerId: string = yield select(selectCustomerId);
  const token: string = yield select(selectAccessToken);
  const url = `${CENTRAL_API_V3}/customers/${customerId}/fts`;
  const config: ApiClientConfig = { token };

  customerBridge.fts.request();
  try {
    const response: FirstTimeSetupStatus = yield call(client, url, config);
    yield put(fetch.fts.success(response));
    customerBridge.fts.success(response);
  } catch (error) {
    if (error instanceof Error) {
      yield put(fetch.fts.failure(error));
      customerBridge.fts.failure(error);
    }
  } finally {
    if (yield cancelled()) {
      source.abort('cancelled');
      yield put(fetch.fts.cancel());
    }
  }
}

function* pollCustomer(action: ReturnType<typeof poll.start>): SagaIterator {
  const isPollingActive = yield select(selectIsPolling);
  if (isPollingActive) {
    return;
  }

  const customerId: string | undefined = yield select(selectCustomerId);
  if (!customerId) {
    return;
  }

  yield put(poll.started());
  customerBridge.polling.started();

  const { intervalMs = 10000, predicate } = action.payload;
  try {
    while (true) {
      yield put(fetch.customerRefresh.request({ uuid: customerId }));
      const result = yield race({
        success: take(CustomerActionTypes.FETCH_REFRESH_SUCCESS),
        failure: take(CustomerActionTypes.FETCH_REFRESH_ERROR),
      });

      if (result.success) {
        const customerData: Customer.Get.Customer | null = yield select(selectCustomerData);
        if (customerData && predicate(customerData)) {
          yield put(fetchMeAsync.request());
          yield put(poll.stop());
          customerBridge.polling.stopped();
          return;
        }
      }
      const { stopped } = yield race({
        delay: delay(intervalMs),
        stopped: take(CustomerActionTypes.POLL_CUSTOMER_STOP),
      });
      if (stopped) {
        customerBridge.polling.stopped();
        return;
      }
    }
  } catch (error) {
    console.error('Error in customer polling saga:', error);
    yield put(poll.stop());
    customerBridge.polling.stopped();
  } finally {
    if (yield cancelled()) {
      yield put(poll.stop());
      customerBridge.polling.stopped();
    }
  }
}

export function* watchFetchCustomer<T>(): Generator<ForkEffect<never>, void, T> {
  yield takeLatest(CustomerActionTypes.FETCH_REQUEST, fetchCustomer);
}

export function* watchFetchCustomerNames<T>(): Generator<ForkEffect<never>, void, T> {
  yield takeLatest(CustomerActionTypes.FETCH_NAMES, fetchCustomerNames);
}

export function* watchFetchCustomerRefresh<T>(): Generator<ForkEffect<never>, void, T> {
  yield takeLatest(CustomerActionTypes.FETCH_REFRESH, fetchCustomerRefresh);
}

export function* watchFetchFtsStatus<T>(): Generator<ForkEffect<never>, void, T> {
  yield takeLatest(CustomerActionTypes.FETCH_REQUEST_FTS, fetchFtsStatus);
}

export function* watchPollCustomerStart<T>(): Generator<ForkEffect<never>, void, T> {
  yield takeLatest(CustomerActionTypes.POLL_CUSTOMER_START, pollCustomer);
}

export function* customerSaga<T>(): Generator<AllEffect<ForkEffect<void>>, void, T> {
  yield all([
    fork(watchFetchCustomer),
    fork(watchFetchCustomerNames),
    fork(watchFetchCustomerRefresh),
    fork(watchFetchFtsStatus),
    fork(watchPollCustomerStart),
  ]);
}
